import { COLORS } from '@hollowveil/shared';
import type { ModelProps, ModelRegistry } from './types';

export function ChestModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.3, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.8, 0.5, 0.5]} />
        <meshStandardMaterial
          color={colors.wood || colors.accent}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Lid */}
      <mesh position={[0, 0.65, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.85, 0.2, 0.55]} />
        <meshStandardMaterial
          color={colors.wood || colors.accent}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Lock */}
      <mesh position={[0, 0.4, 0.26]} castShadow>
        <boxGeometry args={[0.15, 0.15, 0.05]} />
        <meshStandardMaterial
          color={colors.gold || colors.highlight}
          metalness={0.8}
          roughness={0.2}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function BarrelModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.4, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.35, 0.4, 0.8, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B4513' : '#5a3a5a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Metal bands */}
      {[0.15, 0.65].map((y, i) => (
        <mesh key={i} position={[0, y, 0]}>
          <torusGeometry args={[0.38, 0.03, 8, 16]} />
          <meshStandardMaterial color={world === 'real' ? '#4a4a4a' : '#3a3a5a'} metalness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

export function CrateModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.35, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.7, 0.7, 0.7]} />
        <meshStandardMaterial
          color={world === 'real' ? '#A0522D' : '#5a4a6a'}
          roughness={0.85}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Cross planks */}
      <mesh position={[0, 0.35, 0.36]} castShadow>
        <boxGeometry args={[0.6, 0.08, 0.02]} />
        <meshStandardMaterial color={world === 'real' ? '#8B4513' : '#4a3a5a'} />
      </mesh>
      <mesh position={[0, 0.35, 0.36]} rotation={[0, 0, Math.PI / 4]} castShadow>
        <boxGeometry args={[0.5, 0.06, 0.02]} />
        <meshStandardMaterial color={world === 'real' ? '#8B4513' : '#4a3a5a'} />
      </mesh>
    </group>
  );
}

export function BenchModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Seat */}
      <mesh position={[0, 0.35, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.2, 0.08, 0.4]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B7355' : '#5a5a7a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Legs */}
      {[[-0.5, 0], [0.5, 0]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.15, z]} castShadow>
          <boxGeometry args={[0.08, 0.3, 0.35]} />
          <meshStandardMaterial color={world === 'real' ? '#6B4423' : '#4a4a6a'} />
        </mesh>
      ))}
    </group>
  );
}

export function LanternModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Frame */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[0.2, 0.4, 0.2]} />
        <meshStandardMaterial
          color={world === 'real' ? '#2a2a2a' : '#3a3a5a'}
          metalness={0.7}
          roughness={0.3}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Glass */}
      <mesh position={[0, 0.3, 0]}>
        <boxGeometry args={[0.15, 0.3, 0.15]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FFD700' : '#7B68EE'}
          emissive={world === 'real' ? '#FFA500' : '#6A5ACD'}
          emissiveIntensity={0.8}
          transparent
          opacity={0.7}
        />
      </mesh>
      {/* Hook */}
      <mesh position={[0, 0.55, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 0.1, 8]} />
        <meshStandardMaterial color="#3a3a3a" metalness={0.8} />
      </mesh>
    </group>
  );
}

export function TorchStandModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Stand */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.08, 1, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#4a3a2a' : '#3a3a4a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Base */}
      <mesh position={[0, 0.05, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.18, 0.1, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#3a3a3a' : '#2a2a3a'} metalness={0.5} />
      </mesh>
      {/* Flame holder */}
      <mesh position={[0, 1.05, 0]}>
        <cylinderGeometry args={[0.1, 0.06, 0.15, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#5a4a3a' : '#4a3a5a'} />
      </mesh>
      {/* Flame */}
      <mesh position={[0, 1.2, 0]}>
        <coneGeometry args={[0.08, 0.2, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FF6600' : '#9370DB'}
          emissive={world === 'real' ? '#FF4500' : '#8A2BE2'}
          emissiveIntensity={2}
        />
      </mesh>
      <pointLight
        position={[0, 1.2, 0]}
        color={world === 'real' ? '#FF6600' : '#9370DB'}
        intensity={3}
        distance={5}
      />
    </group>
  );
}

export function StatueModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.6, 0.3, 0.6]} />
        <meshStandardMaterial
          color={world === 'real' ? '#808080' : '#6a6a8a'}
          roughness={0.6}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Body */}
      <mesh position={[0, 0.7, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.2, 0.8, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#909090' : '#7a7a9a'}
          roughness={0.5}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 1.2, 0]} castShadow>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#909090' : '#7a7a9a'}
          roughness={0.5}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function FountainModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Basin */}
      <mesh position={[0, 0.2, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.8, 0.9, 0.4, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#708090' : '#5a6a8a'}
          roughness={0.4}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Water */}
      <mesh position={[0, 0.35, 0]}>
        <cylinderGeometry args={[0.7, 0.7, 0.1, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#4169E1' : '#9370DB'}
          transparent
          opacity={0.6}
          roughness={0.1}
        />
      </mesh>
      {/* Center pillar */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 0.6, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#808080' : '#6a6a8a'}
          roughness={0.5}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Top bowl */}
      <mesh position={[0, 0.95, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.15, 0.15, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#708090' : '#5a6a8a'}
          roughness={0.4}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function WellModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base ring */}
      <mesh position={[0, 0.3, 0]} castShadow receiveShadow>
        <torusGeometry args={[0.5, 0.2, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#696969' : '#5a5a7a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Inner dark */}
      <mesh position={[0, 0.1, 0]}>
        <cylinderGeometry args={[0.35, 0.35, 0.3, 12]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Roof supports */}
      {[[0.45, 0], [-0.45, 0]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.8, z]} castShadow>
          <boxGeometry args={[0.08, 1, 0.08]} />
          <meshStandardMaterial color={world === 'real' ? '#8B4513' : '#5a4a6a'} />
        </mesh>
      ))}
      {/* Roof */}
      <mesh position={[0, 1.4, 0]} castShadow>
        <coneGeometry args={[0.6, 0.4, 4]} />
        <meshStandardMaterial
          color={world === 'real' ? '#A0522D' : '#6a5a7a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function CandlesModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {[[0, 0], [0.12, 0.05], [-0.1, 0.08]].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          {/* Candle body */}
          <mesh position={[0, 0.1 + i * 0.05, 0]} castShadow>
            <cylinderGeometry args={[0.04, 0.05, 0.2 + i * 0.1, 8]} />
            <meshStandardMaterial
              color={world === 'real' ? '#FFF8DC' : '#E6E6FA'}
              roughness={0.8}
              transparent={isOccluding}
              opacity={opacity}
            />
          </mesh>
          {/* Flame */}
          <mesh position={[0, 0.25 + i * 0.08, 0]}>
            <coneGeometry args={[0.02, 0.06, 6]} />
            <meshStandardMaterial
              color={world === 'real' ? '#FFA500' : '#DA70D6'}
              emissive={world === 'real' ? '#FF6600' : '#BA55D3'}
              emissiveIntensity={2}
            />
          </mesh>
        </group>
      ))}
      <pointLight
        position={[0, 0.3, 0]}
        color={world === 'real' ? '#FFA500' : '#DA70D6'}
        intensity={1}
        distance={3}
      />
    </group>
  );
}

export function SignpostModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Post */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <boxGeometry args={[0.12, 1.2, 0.12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#6B4423' : '#4a3a5a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Sign board */}
      <mesh position={[0.25, 1, 0]} castShadow>
        <boxGeometry args={[0.5, 0.25, 0.05]} />
        <meshStandardMaterial
          color={world === 'real' ? '#DEB887' : '#7a6a8a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function FenceModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Posts */}
      {[[-0.4, 0], [0.4, 0]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.4, z]} castShadow>
          <boxGeometry args={[0.1, 0.8, 0.1]} />
          <meshStandardMaterial
            color={world === 'real' ? '#8B4513' : '#5a4a6a'}
            roughness={0.9}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
      {/* Rails */}
      {[0.25, 0.55].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} castShadow>
          <boxGeometry args={[0.9, 0.08, 0.05]} />
          <meshStandardMaterial
            color={world === 'real' ? '#A0522D' : '#6a5a7a'}
            roughness={0.85}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
    </group>
  );
}

export function NpcModel({ world, interactive, hovered }: ModelProps) {
  return (
    <group position={[0, 0, 0]}>
      {/* Robe body */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <coneGeometry args={[0.4, 1.2, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#4a3a6a' : '#6a4a8a'}
          roughness={0.7}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 1.35, 0]} castShadow>
        <sphereGeometry args={[0.2, 12, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#e8d4b8' : '#c8b4a8'}
          roughness={0.6}
        />
      </mesh>
      {/* Hood */}
      <mesh position={[0, 1.45, -0.05]} castShadow>
        <sphereGeometry args={[0.25, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={world === 'real' ? '#3a2a5a' : '#5a3a7a'}
          roughness={0.8}
        />
      </mesh>
      {/* Staff */}
      <mesh position={[0.35, 0.8, 0]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 1.6, 6]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.8} />
      </mesh>
      {/* Staff orb */}
      <mesh position={[0.35, 1.65, 0]} castShadow>
        <octahedronGeometry args={[0.08]} />
        <meshStandardMaterial
          color="#7fb3d5"
          emissive="#7fb3d5"
          emissiveIntensity={0.5}
          roughness={0.2}
        />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.06, 1.38, 0.15]}>
        <sphereGeometry args={[0.03, 6, 6]} />
        <meshBasicMaterial color="#333333" />
      </mesh>
      <mesh position={[0.06, 1.38, 0.15]}>
        <sphereGeometry args={[0.03, 6, 6]} />
        <meshBasicMaterial color="#333333" />
      </mesh>
      {interactive && hovered && (
        <pointLight position={[0, 1, 0]} color="#7fb3d5" intensity={2} distance={3} />
      )}
    </group>
  );
}

export function SpectralRemnantModel({ world }: ModelProps) {
  return (
    <group position={[0, 0.8, 0]}>
      {/* Ghost body - dome top */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <sphereGeometry args={[0.45, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color="#7ecfff"
          transparent
          opacity={world === 'spectral' ? 0.85 : 0.1}
          emissive="#44aaff"
          emissiveIntensity={world === 'spectral' ? 0.6 : 0}
          roughness={0.2}
          side={2}
        />
      </mesh>
      {/* Ghost body - cylinder middle */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <cylinderGeometry args={[0.45, 0.45, 0.5, 16]} />
        <meshStandardMaterial
          color="#7ecfff"
          transparent
          opacity={world === 'spectral' ? 0.85 : 0.1}
          emissive="#44aaff"
          emissiveIntensity={world === 'spectral' ? 0.6 : 0}
          roughness={0.2}
        />
      </mesh>
      {/* Ghost wavy bottom */}
      {[0, 1, 2, 3].map((i) => {
        const angle = (i / 4) * Math.PI * 2;
        const offset = i % 2 === 0 ? 0 : -0.08;
        return (
          <mesh
            key={`wave-${i}`}
            position={[Math.cos(angle) * 0.25, -0.2 + offset, Math.sin(angle) * 0.25]}
            castShadow
          >
            <coneGeometry args={[0.18, 0.3, 6]} />
            <meshStandardMaterial
              color="#7ecfff"
              transparent
              opacity={world === 'spectral' ? 0.8 : 0.1}
              emissive="#44aaff"
              emissiveIntensity={world === 'spectral' ? 0.5 : 0}
              roughness={0.2}
            />
          </mesh>
        );
      })}
      {/* Eyes */}
      {world === 'spectral' && (
        <>
          <mesh position={[-0.15, 0.3, 0.38]}>
            <sphereGeometry args={[0.1, 12, 12]} />
            <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={0.3} />
          </mesh>
          <mesh position={[0.15, 0.3, 0.38]}>
            <sphereGeometry args={[0.1, 12, 12]} />
            <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={0.3} />
          </mesh>
          {/* Pupils */}
          <mesh position={[-0.15, 0.3, 0.45]}>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color="#1a1a8a" emissive="#2222aa" emissiveIntensity={0.5} />
          </mesh>
          <mesh position={[0.15, 0.3, 0.45]}>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color="#1a1a8a" emissive="#2222aa" emissiveIntensity={0.5} />
          </mesh>
          <pointLight position={[0, 0.2, 0]} color="#66ccff" intensity={2} distance={5} />
        </>
      )}
    </group>
  );
}

export function MushroomModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Stem */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <cylinderGeometry args={[0.08, 0.1, 0.3, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#F5DEB3' : '#c0a0d0'}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Cap */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <sphereGeometry args={[0.2, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={world === 'real' ? '#DC143C' : '#9932CC'}
          roughness={0.6}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Spots */}
      {[0, 1, 2, 3].map((i) => (
        <mesh
          key={i}
          position={[
            Math.cos(i * Math.PI / 2) * 0.12,
            0.38,
            Math.sin(i * Math.PI / 2) * 0.12
          ]}
        >
          <sphereGeometry args={[0.04, 6, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#FFFFFF' : '#E6E6FA'} />
        </mesh>
      ))}
    </group>
  );
}

export function VinesModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {[0, 1, 2].map((i) => (
        <mesh
          key={i}
          position={[(i - 1) * 0.15, 0.4, 0]}
          rotation={[0.1 * (i - 1), 0, 0.05 * (i - 1)]}
          castShadow
        >
          <cylinderGeometry args={[0.02, 0.015, 0.8 + i * 0.1, 6]} />
          <meshStandardMaterial
            color={world === 'real' ? '#228B22' : '#6B8E23'}
            roughness={0.8}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
      {/* Leaves */}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh
          key={i}
          position={[
            (Math.random() - 0.5) * 0.3,
            0.2 + i * 0.15,
            (Math.random() - 0.5) * 0.1
          ]}
          rotation={[0, Math.random() * Math.PI, Math.PI / 4]}
        >
          <planeGeometry args={[0.1, 0.06]} />
          <meshStandardMaterial
            color={world === 'real' ? '#32CD32' : '#9ACD32'}
            side={2}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
    </group>
  );
}

export function PotModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.25, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.2, 0.25, 0.5, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#CD853F' : '#8B7355'}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Rim */}
      <mesh position={[0, 0.52, 0]}>
        <torusGeometry args={[0.2, 0.04, 8, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#D2691E' : '#9B7B65'}
          roughness={0.6}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function RugModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <planeGeometry args={[1.5, 1]} />
      <meshStandardMaterial
        color={world === 'real' ? '#8B0000' : '#4B0082'}
        roughness={0.9}
        transparent={isOccluding}
        opacity={opacity}
      />
    </mesh>
  );
}

export const decorativeModels: ModelRegistry = {
  chest: ChestModel,
  barrel: BarrelModel,
  crate: CrateModel,
  bench: BenchModel,
  lantern: LanternModel,
  torch_stand: TorchStandModel,
  statue: StatueModel,
  fountain: FountainModel,
  well: WellModel,
  candles: CandlesModel,
  signpost: SignpostModel,
  fence: FenceModel,
  npc: NpcModel,
  sage: NpcModel,
  spectral_remnant: SpectralRemnantModel,
  mushroom: MushroomModel,
  vines: VinesModel,
  pot: PotModel,
  rug: RugModel,
};
